import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonDropdown, Icon, Typography } from "@/components/atoms";
import { BulkBookingClient } from "@/components/organisms/BulkBooking/BulkBookingContext";
import { BulkBookingModal } from "@/components/organisms/BulkBooking/BulkBookingModal";
import { BulkUploadMenuSection } from "@/components/organisms/BulkBooking/BulkUploadMenuSection";
import { ClientPickerModal, ClientPickerProfile } from "@/components/organisms/ClientPickerModal/ClientPickerModal";
import { useAppDispatch } from "@/redux/hooks";
import { clearBooking } from "@/redux/slices/clientBooking/clientBookingSlice";
import { useCanManageAsAssistant } from "./ClientNavLinks";

interface ClientBookTripButtonProps {
  /** "header" = desktop nav (sm button); "drawer" = full-width entry in the mobile drawer footer (lg button). */
  variant?: "header" | "drawer";
}

/**
 * The client portal's "Book a Trip" nav control — a split button (DEV-1215): the left half books
 * directly, the caret opens the alternatives plus the bulk-upload options. Styled from the same
 * caret theme as the Client Manager Actions button, so the two look identical.
 *
 * Plain clients book for themselves. Assistant accounts (EA / Travel Manager, DEV-1296) also get
 * "Book for your Clients", which opens the managed-client picker and routes to the `book-for/:accessId`
 * scoped sheet.
 *
 * Not yet built: the EA design also lists "Book for a Guest" — an unrelated new booking path rather
 * than part of bulk upload. It drops into this menu when it lands.
 */
export const ClientBookTripButton = ({ variant = "header" }: ClientBookTripButtonProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const canManage = useCanManageAsAssistant();
  // "book" navigates to the scoped sheet; "bulk" opens the wizard for that client. Undefined = closed.
  const [picking, setPicking] = useState<"book" | "bulk">();
  const [bulkClient, setBulkClient] = useState<BulkBookingClient>();
  // A plain client bulk-uploads for themselves, so there's no client to pick.
  const [selfBulkOpen, setSelfBulkOpen] = useState(false);

  const size = variant === "drawer" ? "lg" : "sm";
  const widthClassName = variant === "drawer" ? "w-full" : undefined;
  // The drawer button sits in the sheet's bottom footer, so its menu must open upward, not down.
  const menuPosition = variant === "drawer" ? "top-end" : "bottom-end";

  // Self-service entry — reset any in-flight draft first (as the managed-client path does) so a previous
  // (possibly abandoned) attempt's locations/passenger don't carry into a fresh booking.
  const bookForSelf = () => {
    dispatch(clearBooking());
    navigate("/client/booking");
  };

  // Picking a managed client resets any in-flight draft (as the operator flow does) and routes to
  // the assistant-scoped booking sheet keyed on the access-link id.
  const handleSelect = (accessId: string, profile?: ClientPickerProfile) => {
    const purpose = picking;
    setPicking(undefined);

    if (purpose === "bulk") {
      // For an assistant the picker's id is the access-link id, so the import scopes on the
      // profile's `accountId` (the client's account uuid) instead.
      if (!profile?.accountId) return;
      setBulkClient({ accountId: profile.accountId, name: profile.name, avatarSrc: profile.avatarSrc });
      return;
    }

    dispatch(clearBooking());
    navigate(`/client/book-for/${accessId}`);
  };

  // An assistant has no trips of their own by default, so their primary action is the client picker;
  // a plain client goes straight into self-service.
  const primaryAction = canManage ? () => setPicking("book") : bookForSelf;

  return (
    <>
      <ButtonDropdown
        startIcon="Add"
        size={size}
        position={menuPosition}
        className={widthClassName}
        menuClassName="w-[298px]"
        onClick={primaryAction}
      >
        <>Book a Trip</>
        <>
          {canManage ? (
            <>
              <ButtonDropdown.Item onClick={() => setPicking("book")} className="w-[298px]">
                <Icon name="Add" className="mr-2 text-neutral-black" size="md" />
                <Typography className="text-neutral-black">Book for your Clients</Typography>
              </ButtonDropdown.Item>
              <ButtonDropdown.Item onClick={bookForSelf} className="w-[298px]">
                <Icon name="Add" className="mr-2 text-neutral-black" size="md" />
                <Typography className="text-neutral-black">Book for yourself</Typography>
              </ButtonDropdown.Item>
            </>
          ) : (
            <ButtonDropdown.Item onClick={bookForSelf} className="w-[298px]">
              <Icon name="Add" className="mr-2 text-neutral-black" size="md" />
              <Typography className="text-neutral-black">Book a Trip</Typography>
            </ButtonDropdown.Item>
          )}
          <BulkUploadMenuSection onUpload={() => (canManage ? setPicking("bulk") : setSelfBulkOpen(true))} />
        </>
      </ButtonDropdown>

      {canManage && (
        <ClientPickerModal
          source="assistant"
          open={!!picking}
          onClose={() => setPicking(undefined)}
          onSelect={handleSelect}
          title={picking === "bulk" ? "Select a Profile to Upload a Bulk Booking" : undefined}
          description={picking === "bulk" ? "Choose a client to upload bulk booking" : undefined}
        />
      )}

      {/* Assistants import for a picked client; a plain client imports for themselves. */}
      <BulkBookingModal
        open={canManage ? !!bulkClient : selfBulkOpen}
        onClose={() => (canManage ? setBulkClient(undefined) : setSelfBulkOpen(false))}
        client={bulkClient}
      />
    </>
  );
};
